import { queryClient } from '../../queryClient';
import { type PartialAppInfoResponse } from './PartialAppInfoResponse';

import { useQuery } from '@tanstack/react-query';

function isPartialAppInfoResponse(response: unknown): response is PartialAppInfoResponse {
  const data = response as PartialAppInfoResponse;

  const {
    app: [app],
  } = data;

  return 'client_id' in app && 'client_secret' in app;
}

interface UseAppInfoOptions {
  lang?: string;
  css?: boolean;
}

function getQueryKey(id: string, options: UseAppInfoOptions) {
  const { lang = 'en-US', css = true } = options;

  return ['appInfo', id, lang, css];
}

function getQueryFn(id: string, options: UseAppInfoOptions) {
  return async () => {
    const { lang = 'en-US', css = true } = options;

    const url = new URL('https://cms.travpromobile.com/api/app/app-info/');
    url.searchParams.set('app_id', id);
    url.searchParams.set('format', 'json');
    url.searchParams.set('lang', lang);
    url.searchParams.set('css', css.toString());

    const response = await fetch(url.toString());

    if (!response.ok) throw new Error('Failed to fetch app info');

    const data = (await response.json()) as unknown;

    if (!isPartialAppInfoResponse(data)) {
      return null;
    }

    return data.app[0];
  };
}

/**
 * Partial implementation of the SDK's useAppInfo hook.
 * Just until I can get around to migrating API calls to the SDK.
 */
export function useAppInfo(id: string = import.meta.env.VITE_APP_ID, options: UseAppInfoOptions = {}) {
  return useQuery({
    queryKey: getQueryKey(id, options),
    queryFn: getQueryFn(id, options),
  });
}

export function getAppInfo(id: string = import.meta.env.VITE_APP_ID, options: UseAppInfoOptions = {}) {
  return queryClient.ensureQueryData({
    queryKey: getQueryKey(id, options),
    queryFn: getQueryFn(id, options),
  });
}
